import { useState, useEffect } from 'preact/hooks';
import Beans3 from '@/beans';
import { Utils, riperStorage } from '@/base/helpers';

function orderRuleByPosition(rules) {
  const sortedRules = rules.sort((ruleA, ruleB) => ruleA.settings.position - ruleB.settings.position);
  return sortedRules;
}

function useActivity(account) {
  const [activityPendingList, setActivityPendingList] = useState([]);
  const [activityList, setActivityList] = useState([]);

  useEffect(() => {
    if (account) {
      Beans3.get({ path: 'ultimate/account/current/history' }).then((activity) => {
        setActivityList(activity.data);
      });

      Beans3.get({ path: 'liana/debit/?status=pending' }).then((activity) => {
        setActivityPendingList(activity.data);
      });
    }
  }, []);

  return [activityPendingList, activityList];
}

function useRule({ account, Strings, display }) {
  const [rulesList, setRulesList] = useState([]);

  useEffect(() => {
    if (!account) {
      Beans3.get({ path: `liana/rule${riperStorage.currencyQuery}?limit=30` }).then(({ data }) =>
        setRulesList(orderRuleByPosition(data))
      );
    } else {
      Promise.all([
        Beans3.get({ path: `liana/rule${riperStorage.currencyQuery}?limit=30` }),
        Beans3.get({ path: `lattice/shopper/${account.id}/`, namespace: 'trellis' }),
      ])
        .then(([rules, shopperData]) => {
          const ruleAction = rules.data.map((rule) =>
            Utils.getRuleAction({ rule, shopperData, account, Strings, display })
          );
          setRulesList(orderRuleByPosition(ruleAction));
        })
        .catch(console.log);
    }
  }, []);

  return rulesList;
}

function useTemplet({ ruleUid }) {
  const [state, setState] = useState({
    templet: riperStorage.templets[ruleUid],
    templetError: false,
    templetLoading: true,
  });

  function handleError(error) {
    console.log(error);
    setState({
      ...state,
      templetLoading: false,
      templetError: true,
    });
  }

  useEffect(() => {
    let query = null;
    if (ruleUid === 'rule:liana:display' && riperStorage.args.lianaPageLocalTemplet) {
      query = fetch(riperStorage.args.lianaPageLocalTemplet).then((response) => response.json());
    } else if (ruleUid === 'rule:liana:display_cart' && riperStorage.args.lianaCartLocalTemplet) {
      query = fetch(riperStorage.args.lianaCartLocalTemplet).then((response) => response.json());
    } else {
      query = new Promise((resolve) => {
        resolve(riperStorage.templets[ruleUid]);
      });
    }
    query
      .then((templet) => {
        setState({
          templet,
          templetError: false,
          templetLoading: false,
        });
      })
      .catch(handleError);
  }, []);

  return state;
}

function useLeaderboard(account) {
  const [currentShopper, setCurrentShopper] = useState(null);
  const [allLeaderboardMembers, setAllLeaderboardMembers] = useState([]);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (account) {
      Beans3.get({ path: `leaderboard/shopper/${account.id}/`, namespace: 'trellis' })
        .then((res) => {
          setIsInstalled(true);
          setCurrentShopper(res);
        })
        .catch((err) => {
          if (err.code === 401) {
            setIsInstalled(false);
          }
          setCurrentShopper(null);
        });
    }
    Beans3.get({ path: 'leaderboard/shopper?limit=30', namespace: 'trellis' })
      .then((res) => {
        const data = res.data;
        data.sort((a, b) => b.beans_cumulative - a.beans_cumulative);

        data.forEach((item, index) => {
          item.rank = index + 1;
          item.beans_cumulative = item.beans_cumulative.toLocaleString();
        });
        setAllLeaderboardMembers(data);
        setIsInstalled(true);
      })
      .catch((err) => {
        if (err.code === 401) {
          setIsInstalled(false);
        }
        setAllLeaderboardMembers([]);
      });
  }, [account]);

  return [isInstalled, currentShopper, allLeaderboardMembers];
}

function useVIPTiers(account, display) {
  const [shopperTier, setShopperTier] = useState(null);
  const [tiers, setTiers] = useState(display.tiers || []);

  useEffect(() => {
    // Tiers are already fetched and set in Liana.init, just use cached data
    setTiers(display.tiers || []);

    // If account exists and has tier data from init, derive shopperTier
    if (account && account.tier && display.tiers) {
      const shopperActiveTier = display.tiers.find((tier) => tier.level === account.tier.level) || null;

      if (shopperActiveTier) {
        const vipShopper = {
          tier: shopperActiveTier,
          tier_level: account.tier.level,
          tier_expiring_date: account.tier_expiring,
          tier_beans_cumulative: account.tier_beans_rolling,
        };
        setShopperTier(vipShopper);
      } else {
        setShopperTier(null);
      }
    } else {
      setShopperTier(null);
    }
  }, [account, display]);

  return { tiers, shopperTier };
}

export { useActivity, useRule, useTemplet, useLeaderboard, useVIPTiers };
